<template>
  <div class="column-mapper">
  <Card>
      <template #title>
        <div class="flex align-items-center">
          <i class="pi pi-sitemap mr-2"></i>
          Маппинг колонок Excel
        </div>
      </template>

      <template #content>
        <div v-if="!columns || columns.length === 0" class="empty-state">
          <div class="text-center py-4">
            <i class="pi pi-file-excel text-4xl text-green-400 mb-3"></i>
            <h4 class="text-600 mb-2">Файл не загружен</h4>
            <p class="text-500">Загрузите Excel файл для настройки маппинга колонок</p>
          </div>
        </div>

        <div v-else>
          <!-- Информация о структуре файла -->
          <div v-if="structureInfo" class="structure-info mb-4">
            <Card>
              <template #title>
                <div class="flex align-items-center">
                  <i class="pi pi-info-circle mr-2"></i>
                  Структура файла
                </div>
              </template>
              <template #content>
                <div class="grid">
                  <div class="col-12 md:col-3">
                    <div class="stat-card">
                      <div class="stat-number text-blue-600">{{ structureInfo.header_row + 1 }}</div>
                      <div class="stat-label">Строка заголовков</div>
                    </div>
                  </div>
                  <div class="col-12 md:col-3">
                    <div class="stat-card">
                      <div class="stat-number text-green-600">{{ structureInfo.total_rows }}</div>
                      <div class="stat-label">Всего строк</div>
                    </div>
                  </div>
                  <div class="col-12 md:col-3">
                    <div class="stat-card">
                      <div class="stat-number text-purple-600">{{ structureInfo.total_columns }}</div>
                      <div class="stat-label">Всего колонок</div>
                    </div>
                  </div>
                  <div class="col-12 md:col-3">
                    <div class="stat-card">
                      <div class="stat-number" :class="structureInfo.has_images ? 'text-orange-600' : 'text-gray-500'">
                        <i :class="structureInfo.has_images ? 'pi pi-image' : 'pi pi-times'"></i>
                      </div>
                      <div class="stat-label">Изображения</div>
                    </div>
                  </div>
                </div>
                
                <!-- Показываем извлеченные изображения если есть -->
                <div v-if="extractedImages && Object.keys(extractedImages).length > 0" class="mt-4">
                  <h3 class="text-900 mb-3">Извлеченные изображения ({{ Object.keys(extractedImages).length }})</h3>
                  <div class="image-preview-grid">
                    <div 
                      v-for="(imageData, imageName) in extractedImages" 
                      :key="imageName"
                      class="image-preview-item"
                    >
                      <img 
                        :src="`data:image/jpeg;base64,${imageData}`" 
                        :alt="imageName"
                        class="preview-image"
                      />
                      <div class="image-name">{{ imageName }}</div>
                    </div>
                  </div>
                </div>
              </template>
            </Card>
          </div>

          <!-- Примеры данных -->
          <div v-if="sampleData && sampleData.length > 0" class="sample-data mb-4">
            <Card>
              <template #title>
                <div class="flex align-items-center">
                  <i class="pi pi-eye mr-2"></i>
                  Предварительный просмотр данных
                </div>
              </template>
              <template #content>
                <DataTable 
                  :value="sampleData" 
                  class="p-datatable-sm"
                  :scrollable="true"
                  scrollHeight="200px"
                >
                  <Column 
                    v-for="column in Object.keys(sampleData[0] || {})" 
                    :key="column"
                    :field="column" 
                    :header="column"
                    style="min-width: 150px"
                  >
                    <template #body="slotProps">
                      <span class="sample-data-cell">
                        {{ formatSampleValue(slotProps.data[column]) }}
                      </span>
                    </template>
                  </Column>
                </DataTable>
              </template>
            </Card>
          </div>

          <div class="mapping-info mb-4">
            <Message severity="info" :closable="false">
              <div class="flex align-items-start">
                <i class="pi pi-info-circle mr-2 mt-1"></i>
                <div>
                  <strong>ИИ предложил маппинг колонок.</strong>
                  <br>
                  Проверьте соответствия и скорректируйте при необходимости.
                  Незамаппенные колонки будут пропущены при импорте.
                </div>
              </div>
            </Message>
          </div>

          <div class="mapping-stats mb-3">
            <div class="grid">
              <div class="col-12 md:col-4">
                <div class="stat-card">
                  <div class="stat-number text-green-600">{{ mappedCount }}</div>
                  <div class="stat-label">Определено</div>
                </div>
              </div>
              <div class="col-12 md:col-4">
                <div class="stat-card">
                  <div class="stat-number text-orange-600">{{ unmappedCount }}</div>
                  <div class="stat-label">Ненайдено</div>
                </div>
              </div>
              <div class="col-12 md:col-4">
                <div class="stat-card">
                  <div class="stat-number text-blue-600">{{ totalColumns }}</div>
                  <div class="stat-label">Всего колонок</div>
                </div>
              </div>
            </div>
          </div>

          <div class="mapping-table">
            <DataTable
              :value="mappingItems"
              :paginator="false"
              class="p-datatable-sm mapping-table-full"
              :scrollable="false"
            >
              <Column field="dbField" header="Поле базы данных" style="min-width: 220px">
                <template #body="slotProps">
                  <div class="db-field-cell">
                    <i :class="getFieldIcon(slotProps.data.dbField)" class="mr-2"></i>
                    <div>
                      <strong>{{ getDbFieldLabel(slotProps.data.dbField) }}</strong>
                      <small class="block text-500">{{ slotProps.data.dbField }}</small>
                    </div>
                  </div>
                </template>
              </Column>

              <Column field="excelColumn" header="Колонка Excel" style="min-width: 220px">
                <template #body="slotProps">
                  <Dropdown
                    v-model="slotProps.data.excelColumn"
                    :options="excelColumnOptions"
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Выберите колонку Excel"
                    :showClear="true"
                    class="w-full"
                    :disabled="disabled"
                    @change="onMappingChange(slotProps.data, $event)"
                  >
                    <template #option="optionSlot">
                      <div class="excel-column-option">
                        <i class="pi pi-file-excel text-green-500 mr-2"></i>
                        <div class="flex flex-column">
                          <span>{{ optionSlot.option.label }}</span>
                          <small v-if="optionSlot.option.sample" class="text-500">{{ optionSlot.option.sample }}</small>
                        </div>
                      </div>
                    </template>
                  </Dropdown>
                </template>
              </Column>

              <Column header="Пример" style="min-width: 180px">
                <template #body="slotProps">
                  <span v-if="slotProps.data.excelColumn" class="sample-data-cell">
                    {{ getSampleData(slotProps.data.excelColumn) }}
                  </span>
                  <span v-else class="text-500">—</span>
                </template>
              </Column>

              <Column header="Тип данных" style="width: 120px">
                <template #body="slotProps">
                  <Badge
                    :value="getFieldType(slotProps.data.dbField)"
                    :severity="getTypeSeverity(slotProps.data.dbField)"
                  />
                </template>
              </Column>

              <Column header="Статус" style="width: 120px">
                <template #body="slotProps">
                  <Badge
                    :value="slotProps.data.excelColumn ? 'Определено' : 'Ненайдено'"
                    :severity="slotProps.data.excelColumn ? 'success' : 'warning'"
                  />
                </template>
              </Column>

              <Column header="Не обновлять" style="width: 140px" headerStyle="text-align: center">
                <template #body="slotProps">
                  <div class="flex justify-content-center align-items-center">
                    <Checkbox 
                      v-model="slotProps.data.protected"
                      :binary="true"
                      :disabled="disabled"
                      @change="onProtectedChange(slotProps.data)"
                    />
                    <i 
                      v-if="slotProps.data.protected && !slotProps.data.excelColumn"
                      v-tooltip.left="'Поле не будет импортироваться и не будет обновляться'"
                      class="pi pi-info-circle ml-2 text-blue-500"
                      style="font-size: 0.9rem"
                    ></i>
                  </div>
                </template>
              </Column>
            </DataTable>
          </div>

          <div v-if="protectedFieldsCount > 0" class="mt-3">
            <Message severity="info" :closable="false">
              <div class="flex align-items-start">
                <i class="pi pi-shield mr-2 mt-1"></i>
                <div>
                  <strong>Защищённые поля ({{ protectedFieldsCount }})</strong>
                  <br>
                  <span v-if="protectedMappedFieldsCount > 0">
                    • Смаппированные ({{ protectedMappedFieldsCount }}): НЕ будут обновляться при повторном импорте.
                  </span>
                  <br v-if="protectedMappedFieldsCount > 0 && protectedUnmappedFieldsCount > 0">
                  <span v-if="protectedUnmappedFieldsCount > 0" class="text-blue-600">
                    • Несмаппированные ({{ protectedUnmappedFieldsCount }}): НЕ будут импортироваться вообще.
                  </span>
                </div>
              </div>
            </Message>
          </div>

          <div class="mapping-actions mt-4 flex justify-content-between align-items-center">
            <div class="flex gap-2">
              <Button
                label="Настройки маппинга"
                icon="pi pi-cog"
                class="p-button-secondary"
                :disabled="disabled"
                @click="showMappingSettings"
              />
              <Button
                label="Очистить все"
                icon="pi pi-times"
                class="p-button-secondary p-button-outlined"
                :disabled="disabled"
                @click="clearAllMappings"
              />
            </div>

            <div class="flex gap-2">
              <Button
                label="Предпросмотр"
                icon="pi pi-eye"
                class="p-button-info"
                :disabled="disabled"
                @click="showPreview"
              />
              <Button
                v-if="stepMode"
                label="Продолжить"
                icon="pi pi-arrow-right"
                class="p-button-success"
                :disabled="disabled || !hasValidMappings"
                @click="emitContinue"
              />
              <Button
                v-else
                label="Применить"
                icon="pi pi-check"
                class="p-button-success"
                :disabled="disabled || !hasValidMappings || isLoading"
                :loading="isLoading"
                @click="applyMappings"
              />
            </div>
          </div>
        </div>
      </template>
    </Card>

    <!-- Диалог настроек маппинга -->
    <MappingSettings
      v-model="mappingSettingsVisible"
      @settings-updated="onMappingSettingsUpdated"
    />
  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue'
import { useToast } from 'primevue/usetoast'
import Card from 'primevue/card'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Button from 'primevue/button'
import Dropdown from 'primevue/dropdown'
import Message from 'primevue/message'
import Badge from 'primevue/badge'
import Checkbox from 'primevue/checkbox'
import MappingSettings from './MappingSettings.vue'
import customColumnsService from '../services/customColumnsService'
import { useProductSchema } from '../composables/useProductSchema'

export default {
  name: 'ColumnMapper',
  components: {
    Card,
    DataTable,
    Column,
    Button,
    Dropdown,
    Message,
    Badge,
    Checkbox,
    MappingSettings
  },
  props: {
    columns: {
      type: Array,
      default: () => []
    },
    initialMapping: {
      type: Object,
      default: () => ({})
    },
    initialProtectedFields: {
      type: Array,
      default: () => []
    },
    sampleData: {
      type: Array,
      default: () => []
    },
    structureInfo: {
      type: Object,
      default: () => null
    },
    extractedImages: {
      type: Object,
      default: () => ({})
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    stepMode: {
      type: Boolean,
      default: false
    }
  },
  emits: ['mapping-changed', 'apply-mappings', 'request-preview', 'continue-step'],
  setup(props, { emit }) {
    const toast = useToast()
    const mappingItems = ref([])
    const mappingSettingsVisible = ref(false)

    // Нативные + кастомные поля БД — единый источник из productSchemaStore.
    // backend/schemas/product_fields.py — источник истины для нативных,
    // CustomColumnDefinition — для кастомных. /products/schema объединяет их.
    const { native: nativeSchema, custom: customSchema, reload: reloadSchema } =
      useProductSchema()

    const _typeOf = (dataType) => {
      if (dataType === 'number') return 'NUMERIC'
      if (dataType === 'int') return 'INTEGER'
      if (dataType === 'float') return 'NUMERIC'
      return 'VARCHAR'
    }

    const dbFields = computed(() => {
      // Из нативной схемы исключаем поля, которые не имеет смысла маппить
      // из Excel при импорте (системные / медиа-через-S3).
      const nativeImportable = nativeSchema.value
        .filter((f) => !['id', 'last_modified', 'custom_fields', 'images',
                         'description', 'ai_description', 'status', 'hs_code'].includes(f.key))
        .map((f) => ({
          label: f.display_name,
          value: f.key,
          type: _typeOf(f.data_type),
        }))
      const customImportable = customSchema.value.map((f) => ({
        label: f.display_name,
        value: `custom:${f.key}`,
        type: _typeOf(f.data_type),
        isCustom: true,
      }))
      return [...nativeImportable, ...customImportable]
    })

    // Загрузка схемы (включая кастомные колонки) и переинициализация маппинга.
    const loadCustomColumns = async () => {
      try {
        await reloadSchema()
        initializeMappings()
      } catch (error) {
        console.error('Error loading product schema:', error)
      }
    }

    const mappedCount = computed(() => {
      return mappingItems.value.filter(item => item.excelColumn).length
    })

    const unmappedCount = computed(() => {
      return mappingItems.value.filter(item => !item.excelColumn).length
    })

    const totalColumns = computed(() => {
      return mappingItems.value.length
    })

    const protectedFieldsCount = computed(() => {
      return mappingItems.value.filter(item => item.protected).length
    })

    const protectedMappedFieldsCount = computed(() => {
      return mappingItems.value.filter(item => item.protected && item.excelColumn).length
    })

    const protectedUnmappedFieldsCount = computed(() => {
      return mappingItems.value.filter(item => item.protected && !item.excelColumn).length
    })

    const hasValidMappings = computed(() => {
      return mappingItems.value.some(item => item.excelColumn)
    })

    const getSampleData = (column) => {
      if (!column) {
        return ''
      }
      if (props.sampleData && props.sampleData.length > 0) {
        const sampleRow = props.sampleData[0]
        if (sampleRow && Object.prototype.hasOwnProperty.call(sampleRow, column)) {
          const value = sampleRow[column]
          if (value === null || value === undefined) {
            return ''
          }
          const text = String(value)
          return text.length > 20 ? `${text.substring(0, 20)}...` : text
        }
      }
      return ''
    }

    const excelColumnOptions = computed(() => {
      return (props.columns || []).map(column => ({
        label: column,
        value: column,
        sample: getSampleData(column)
      }))
    })

    const initializeMappings = () => {
      const previousSelections = mappingItems.value.reduce((acc, item) => {
        if (item.dbField) {
          acc[item.dbField] = {
            excelColumn: item.excelColumn || '',
            protected: item.protected || false
          }
        }
        return acc
      }, {})

      const initialMapping = props.initialMapping || {}
      const initialProtected = new Set(props.initialProtectedFields || [])

      mappingItems.value = dbFields.value.map(field => {
        const prevSelection = previousSelections[field.value]
        const excelColumn = prevSelection && prevSelection.excelColumn
          ? prevSelection.excelColumn
          : initialMapping[field.value] || ''
        const isProtected = prevSelection
          ? prevSelection.protected
          : initialProtected.has(field.value)

        return {
          dbField: field.value,
          excelColumn,
          protected: isProtected
        }
      })
    }

    const onMappingChange = (item, event) => {
      item.excelColumn = event.value || ''
      // Сбрасываем protected если маппинг удален
      if (!item.excelColumn) {
        item.protected = false
      }
      emitMappingChanged()
    }

    const onProtectedChange = (item) => {
      emitMappingChanged()
    }

    const emitMappingChanged = () => {
      const currentMapping = {}
      const protectedFields = []
      
      mappingItems.value.forEach(item => {
        // Добавляем в маппинг только смаппированные поля
        if (item.excelColumn) {
          currentMapping[item.dbField] = item.excelColumn
        }
        
        // Но в protected_fields добавляем ВСЕ защищённые (даже несмаппированные!)
        if (item.protected) {
          protectedFields.push(item.dbField)
        }
      })
      
      emit('mapping-changed', {
        mapping: currentMapping,
        protectedFields: protectedFields
      })
    }

    const clearAllMappings = () => {
      mappingItems.value.forEach(item => {
        item.excelColumn = ''
        item.protected = false
      })
      emitMappingChanged()
    }

    const showPreview = () => {
      const currentMapping = {}
      mappingItems.value.forEach(item => {
        if (item.excelColumn) {
          currentMapping[item.dbField] = item.excelColumn
        }
      })

      if (Object.keys(currentMapping).length === 0) {
        toast.add({
          severity: 'warn',
          summary: 'Предупреждение',
          detail: 'Сначала настройте маппинг колонок',
          life: 3000
        })
        return
      }

      emit('request-preview', currentMapping)
    }

    const applyMappings = () => {
      const finalMapping = {}
      mappingItems.value.forEach(item => {
        if (item.excelColumn) {
          finalMapping[item.dbField] = item.excelColumn
        }
      })

      emit('apply-mappings', finalMapping)
    }

    const emitContinue = () => {
      const finalMapping = {}
      mappingItems.value.forEach(item => {
        if (item.excelColumn) {
          finalMapping[item.dbField] = item.excelColumn
        }
      })

      emit('continue-step', finalMapping)
    }

    const getFieldIcon = (fieldValue) => {
      const iconMap = {
        brand_cp: 'pi pi-building',
        part_number: 'pi pi-hashtag',
        part_name: 'pi pi-tag',
        category: 'pi pi-folder',
        type: 'pi pi-cog',
        size: 'pi pi-expand',
        color: 'pi pi-palette',
        brand: 'pi pi-star',
        producer: 'pi pi-industry',
        gender: 'pi pi-users',
        bridge: 'pi pi-arrows-h',
        temple_length: 'pi pi-arrows-h',
        lens_height: 'pi pi-arrows-v',
        age: 'pi pi-calendar',
        shape: 'pi pi-shape',
        year: 'pi pi-calendar-plus',
        model: 'pi pi-tags',
        material: 'pi pi-box'
      }
      return iconMap[fieldValue] || 'pi pi-circle'
    }

    const getFieldType = (fieldValue) => {
      const field = dbFields.value.find(f => f.value === fieldValue)
      return field ? field.type : ''
    }

    const formatSampleValue = (value) => {
      if (value === null || value === undefined) {
        return '—'
      }
      
      const strValue = String(value)
      if (strValue.length > 50) {
        return strValue.substring(0, 50) + '...'
      }
      
      return strValue
    }

    const getTypeSeverity = (fieldValue) => {
      const type = getFieldType(fieldValue)
      switch (type) {
        case 'VARCHAR': return 'info'
        case 'TEXT': return 'success'
        case 'NUMERIC': return 'warning'
        case 'INTEGER': return 'help'
        default: return 'secondary'
      }
    }

    const getDbFieldLabel = (fieldValue) => {
      const field = dbFields.value.find(f => f.value === fieldValue)
      return field ? field.label : fieldValue
    }

    const showMappingSettings = () => {
      mappingSettingsVisible.value = true
    }

    const onMappingSettingsUpdated = () => {
      toast.add({
        severity: 'success',
        summary: 'Успешно',
        detail: 'Настройки маппинга обновлены. Они будут использоваться при следующей загрузке файлов.',
        life: 5000
      })
    }

  watch(() => props.columns, initializeMappings, { immediate: true })
  watch(() => props.initialMapping, initializeMappings)
  watch(() => props.initialProtectedFields, initializeMappings)

    // Загружаем кастомные колонки при монтировании
    onMounted(() => {
      loadCustomColumns()
    })

    return {
      mappingItems,
      dbFields,
      excelColumnOptions,
      mappedCount,
      unmappedCount,
      totalColumns,
      protectedFieldsCount,
      protectedMappedFieldsCount,
      protectedUnmappedFieldsCount,
      hasValidMappings,
      mappingSettingsVisible,
      onMappingChange,
      onProtectedChange,
      emitMappingChanged,
      clearAllMappings,
      showPreview,
      applyMappings,
      emitContinue,
      getFieldIcon,
      getFieldType,
      getTypeSeverity,
      getSampleData,
      formatSampleValue,
      showMappingSettings,
      onMappingSettingsUpdated,
      getDbFieldLabel
    }
  }
}
</script>

<style scoped>
.column-mapper {
  max-width: 1200px;
  margin: 0 auto;
}

.empty-state {
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mapping-stats {
  margin-bottom: 1rem;
}

.stat-card {
  background: white;
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  text-align: center;
}

.stat-number {
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
}

.stat-label {
  color: #6c757d;
  font-size: 0.9rem;
}

.mapping-table {
  margin-top: 1rem;
}

.db-field-cell {
  display: flex;
  align-items: center;
}

.db-field-cell > div {
  display: flex;
  flex-direction: column;
}

.excel-column-option {
  display: flex;
  align-items: flex-start;
}

.mapping-actions {
  border-top: 1px solid #e9ecef;
  padding-top: 1rem;
}

.image-preview-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
}

.image-preview-item {
  text-align: center;
}

.preview-image {
  width: 100px;
  height: 100px;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid #e9ecef;
  transition: border-color 0.3s;
}

.preview-image:hover {
  border-color: #007bff;
}

.image-name {
  font-size: 0.8rem;
  color: #6c757d;
  margin-top: 0.5rem;
  word-break: break-word;
}

.sample-data-cell {
  font-family: monospace;
  font-size: 0.9rem;
}

.structure-info .stat-card {
  text-align: center;
  padding: 1rem;
}

/* Стили для полноразмерной таблицы маппинга */
.mapping-table-full {
  width: 100% !important;
}

.mapping-table-full .p-datatable-wrapper {
  border-radius: 8px;
  overflow: visible !important;
}

.mapping-table-full .p-datatable-table {
  width: 100% !important;
}

/* Убираем фиксированную высоту для полного отображения всех строк */
.mapping-table-full .p-datatable-scrollable-body {
  max-height: none !important;
  overflow: visible !important;
}

/* Стили для лучшего отображения строк */
.mapping-table-full .p-datatable-tbody > tr {
  border-bottom: 1px solid var(--surface-200);
}

.mapping-table-full .p-datatable-tbody > tr:last-child {
  border-bottom: none;
}

/* Адаптивный отступ для таблицы */
.mapping-table {
  margin-bottom: 2rem;
}
</style>
